import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useCartCount } from "../lib/cart-count";
import { MiniIcon } from "./MiniIcon";

/**
 * Header cart entry · the persistent way into the cart from any find-goods /
 * order flow page (home, catalog, product, …). Renders the shared `cart`
 * MiniIcon with a live unread badge sourced from `useCartCount` (real
 * `itemCount`, never a hard-coded number). The badge hides at count 0 so a
 * logged-out / empty cart shows a clean icon. Tapping navigates to `/cart`.
 *
 * Drop into a `TgHeader`'s `right` slot — standalone or beside another action
 * (favorite / camera) inside a small flex row.
 */
export function CartHeaderButton({ size = 20 }: { size?: number }) {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const count = useCartCount();
  // Cap the visible badge so a huge cart never overflows the 44px header row.
  const label = count > 99 ? "99+" : String(count);

  return (
    <button
      type="button"
      aria-label={t("header.cart_label")}
      onClick={() => navigate({ to: "/cart" as never })}
      style={{
        position: "relative",
        background: "transparent",
        border: 0,
        cursor: "pointer",
        color: "var(--color-primary-fill)",
        padding: 4,
        display: "flex",
        alignItems: "center",
        lineHeight: 1,
      }}
    >
      <MiniIcon name="cart" size={size} />
      {count > 0 && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: -2,
            right: -2,
            minWidth: 16,
            height: 16,
            padding: "0 4px",
            borderRadius: 999,
            background: "var(--color-cta)",
            color: "#fff",
            fontSize: 10,
            fontWeight: 800,
            lineHeight: "16px",
            textAlign: "center",
            boxShadow: "0 0 0 2px var(--color-bg)",
          }}
        >
          {label}
        </span>
      )}
    </button>
  );
}
